import { COUNTRY_SORT_KEYS, type CountryListItem, type CountrySortKey } from "@seagames/contracts";
import { type ColumnDef, DataTable, ListHeader, ListTag, ListToolbar, PrimaryCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { timestampColumn, usageColumn } from "../../components/common/list-columns";
import { type CountryFormTarget, CountryFormModal } from "../../components/countries/country-form-modal";
import { countriesListOptions, countryStatsOptions } from "../../lib/countries/queries";
import { listSearch } from "../../lib/list-page/list-search";
import { sortSelect } from "../../lib/list-page/sort-select";
import { useListPage } from "../../lib/list-page/use-list-page";

export const Route = createFileRoute("/_app/settings/countries")({
	validateSearch: listSearch(COUNTRY_SORT_KEYS, {
		/**
		 * A BOOLEAN here, not the "true"/"false" the API takes on the wire.
		 *
		 * TanStack Router's default search parser JSON-parses each value, so
		 * `?competing=true` reaches this schema as the boolean `true` — an
		 * `z.enum(["true","false"])` never matches it, and `.catch(undefined)`
		 * then drops the filter without a word. The string branch is kept for a
		 * hand-typed or externally-built URL that the parser leaves alone.
		 */
		competing: z.union([z.boolean(), z.enum(["true", "false"]).transform((v) => v === "true")]),
	}),
	component: CountriesPage,
});

/** The filters as the URL holds them. `""` = not set; the URL drops empties. */
interface CountryFilters extends Record<string, unknown> {
	search: string;
	competing: "" | "true" | "false";
}
const EMPTY_FILTERS: CountryFilters = { search: "", competing: "" };

function CountriesPage() {
	const { t } = useTranslation("countries");
	const { t: tc } = useTranslation("common");
	const { formatNumber } = useUiStrings();
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const isSuper = session.active_role === "SUPER_ADMIN";
	const [form, setForm] = useState<CountryFormTarget | null>(null);

	// `competing: false` is a real filter, so none of these can be truthiness checks.
	const page = useListPage({
		search,
		navigate: Route.useNavigate(),
		// Back to the wire form the API validates ("true" / "false").
		listOptions: (q) => countriesListOptions({ ...q, competing: search.competing === undefined ? undefined : search.competing ? "true" : "false" }),
		columnsKey: "countries",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s): CountryFilters => ({ search: s.search ?? "", competing: s.competing === undefined ? "" : s.competing ? "true" : "false" }),
			toUrl: (f) => ({ search: f.search.trim() || undefined, competing: f.competing === "" ? undefined : f.competing === "true" }),
		},
		summary: { range: (v) => t("pagination.summary", v), empty: t("pagination.empty") },
	});
	// Unfiltered counts for the strip and the participation chips — one aggregate statement.
	const stats = useQuery(countryStatsOptions());
	const competingCount = stats.data?.competing;
	const referenceCount = stats.data?.reference_only;

	const total = page.list.data?.total;
	const competingValue = search.competing === undefined ? [] : [search.competing ? "true" : "false"];
	const sort = sortSelect<CountrySortKey>(
		search,
		"name:asc",
		[
			{ value: "name:asc", label: tc("sort.nameAsc") },
			{ value: "name:desc", label: tc("sort.nameDesc") },
			{ value: "ioc_code:asc", label: t("sort.iocAsc") },
			{ value: "ioc_code:desc", label: t("sort.iocDesc") },
			{ value: "iso_code:asc", label: t("sort.isoAsc") },
			{ value: "iso_code:desc", label: t("sort.isoDesc") },
		],
		page.patch,
	);

	const columns: ColumnDef<CountryListItem, CountrySortKey>[] = [
		{
			key: "name",
			header: t("table.country"),
			sortable: true,
			lockVisible: true,
			minWidth: "15rem",
			cell: (c) => (
				<span className="flex min-w-0 items-center gap-[0.75rem]">
					<Flag country={c} />
					<PrimaryCell sub={c.nationality ?? t("table.noDemonym")}>{c.name}</PrimaryCell>
				</span>
			),
		},
		{
			key: "ioc_code",
			header: t("table.ioc"),
			sortable: true,
			width: "7rem",
			// The two codes sit side by side deliberately: seeing MAS next to MYS is
			// what stops someone "correcting" one to match the other.
			cell: (c) =>
				c.ioc_code ? (
					<ListTag title={t("table.iocTitle")} className="font-mono tracking-[0.08em]">
						{c.ioc_code}
					</ListTag>
				) : (
					<span className="text-base-content/30" title={t("table.notCompetingTitle")}>
						—
					</span>
				),
		},
		{
			key: "iso_code",
			header: t("table.iso"),
			sortable: true,
			width: "6rem",
			cell: (c) => (
				<span className="font-mono text-[0.8125rem] tracking-[0.08em] text-gms-ink-2" title={t("table.isoTitle")}>
					{c.iso_code}
				</span>
			),
		},
		{
			key: "calling_code" as CountrySortKey,
			header: t("table.dial"),
			width: "6rem",
			cell: (c) =>
				c.calling_code ? <span className="font-mono text-[0.8125rem] tabular-nums text-gms-ink-2">{c.calling_code}</span> : <span className="text-base-content/30">—</span>,
		},
		usageColumn("people_count", t("table.people"), undefined, "6.5rem"),
		timestampColumn("updated_at", t("table.updated")),
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={t("page.title")}
				count={total === undefined ? undefined : t("page.count", { count: total, n: formatNumber(total) })}
				description={<Trans t={t} i18nKey="page.intro" components={{ strong: <strong className="font-semibold text-gms-ink-2" /> }} />}
				actionLabel={t("page.addCountry")}
				onAction={isSuper ? () => setForm({ mode: "create" }) : undefined}
			/>

			<StatsStrip
				label={tc("list.summary")}
				cells={[
					{ key: "total", label: t("stats.total"), value: total ?? "—", note: page.hasFilters ? t("stats.matching") : undefined },
					...(competingCount !== undefined && referenceCount !== undefined
						? [
								{ key: "competing", label: t("stats.competing"), value: competingCount },
								{ key: "reference", label: t("stats.reference"), value: referenceCount },
							]
						: []),
				]}
			/>

			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("filters.searchPlaceholder")}
				sort={sort}
				chips={[
					{
						kind: "options",
						key: "competing",
						label: t("filters.participation"),
						options: [
							{ value: "true", label: t("filters.competing"), count: competingCount },
							{ value: "false", label: t("filters.notCompeting"), count: referenceCount },
						],
						value: competingValue,
						onChange: (v) => page.patch({ competing: v[0] === undefined ? undefined : v[0] === "true" }),
					},
				]}
				onClearAll={page.clearFilters}
			/>

			<DataTable<CountryListItem, CountrySortKey>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(c) => c.country_id}
				empty={page.emptyState({
					filtered: { title: t("empty.filteredTitle"), description: t("empty.filteredDescription") },
					none: { title: t("empty.noneTitle"), description: t("empty.noneDescription") },
					clearLabel: t("empty.clearFilters"),
				})}
				rowActions={
					isSuper ? (c) => <EditDeleteActions editLabel={t("table.editLabel", { name: c.name })} onEdit={() => setForm({ mode: "edit", country: c })} variant="list" /> : undefined
				}
			/>

			{isSuper && <CountryFormModal target={form} onClose={() => setForm(null)} />}
		</div>
	);
}

/**
 * The flag, or a fallback that is still identifying. `flag_url` is null for
 * every seeded row today, so the fallback is the common case, not the edge —
 * it shows the ISO code rather than a grey square.
 */
function Flag({ country }: { country: CountryListItem }) {
	if (country.flag_url) {
		return <img src={country.flag_url} alt="" aria-hidden loading="lazy" className="h-[1.375rem] w-[2rem] shrink-0 rounded-[0.1875rem] border border-gms-line object-cover" />;
	}
	return (
		<span
			aria-hidden
			className="flex h-[1.375rem] w-[2rem] shrink-0 items-center justify-center rounded-[0.1875rem] border border-gms-line bg-gms-soft font-mono text-[0.625rem] font-semibold tracking-[0.04em] text-gms-ink-3"
		>
			{country.iso_code}
		</span>
	);
}
